import { useState } from "react";
import { ChevronDown } from "lucide-react";
import { Card } from "../ui/card";
import { cn } from "../../lib/utils";
import { homeAccentLink, homeAccentText } from "../../utils/homeBlueTheme";

export interface PoweringPredictionItem {
  title: string;
  value: string;
  meta?: string;
}

export interface PoweringPredictionCardProps {
  items?: PoweringPredictionItem[];
  onRecalibrate?: () => void;
  onLogWorkout?: () => void;
}

const isFingerPrickItem = (title: string) =>
  title.toLowerCase().includes("finger");

const isActivityItem = (title: string) =>
  title.toLowerCase().includes("activity");

const isStaleMeta = (meta?: string) => {
  const lower = meta?.toLowerCase() ?? "";
  return (
    lower.includes("days ago") ||
    lower.includes("recalibrate") ||
    lower.includes("needs attention")
  );
};

const PoweringPredictionCard = ({
  items = [],
  onRecalibrate,
  onLogWorkout,
}: PoweringPredictionCardProps) => {
  const [expanded, setExpanded] = useState(false);

  if (items.length === 0) return null;

  return (
    <Card className="overflow-hidden rounded-2xl border border-border/60 bg-card shadow-lg">
      <div className="p-4">
        <button
          type="button"
          onClick={() => setExpanded((v) => !v)}
          className="flex w-full items-start justify-between gap-3 text-left"
          aria-expanded={expanded}
        >
          <div>
            <div className={cn("text-compact-xs font-bold uppercase tracking-wider", homeAccentText)}>
              Powering This Prediction
            </div>
            <div className="mt-0.5 text-sm font-semibold text-muted-foreground">
              Only what needs attention
            </div>
          </div>
          <ChevronDown
            className={`mt-0.5 h-5 w-5 shrink-0 text-muted-foreground transition-transform ${expanded ? "rotate-180" : ""}`}
          />
        </button>

        {expanded && (
        <div className="mt-3 space-y-2.5 border-t border-border/50 pt-3">
          {items.map((item, idx) => {
            const fingerPrick = isFingerPrickItem(item.title);
            const activity = isActivityItem(item.title);
            const stale = fingerPrick && isStaleMeta(item.meta);

            return (
              <div key={`${item.title}-${idx}`} className="flex items-start gap-2.5">
                <div className="mt-1.5 h-1.5 w-1.5 shrink-0 rounded-full bg-foreground" />
                <div className="flex-1">
                  <span className="text-sm font-semibold text-foreground">
                    {item.title}:
                  </span>{" "}
                  <span className="text-sm text-foreground">{item.value}</span>
                  {item.meta ? (
                    <>
                      {" "}
                      <span className={cn("text-xs font-semibold uppercase tracking-wide", homeAccentText)}>
                        {item.meta}
                      </span>
                    </>
                  ) : null}
                  {activity && onLogWorkout ? (
                    <>
                      {" "}
                      <button
                        type="button"
                        onClick={onLogWorkout}
                        className={cn("text-xs font-semibold uppercase tracking-wide underline-offset-2 hover:underline", homeAccentLink)}
                      >
                        Log workout
                      </button>
                    </>
                  ) : activity ? (
                    <>
                      {" "}
                      <span className={cn("text-xs font-semibold uppercase tracking-wide", homeAccentText)}>
                        Log workout
                      </span>
                    </>
                  ) : null}
                  {stale && onRecalibrate ? (
                    <>
                      {" "}
                      <button
                        type="button"
                        onClick={onRecalibrate}
                        className={cn("text-xs font-semibold uppercase tracking-wide underline-offset-2 hover:underline", homeAccentLink)}
                      >
                        Recalibrate
                      </button>
                    </>
                  ) : null}
                </div>
              </div>
            );
          })}
        </div>
        )}
      </div>
    </Card>
  );
};

export default PoweringPredictionCard;
